import { useEffect, useMemo, useState } from 'react';

import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminBooking, BookingListStatus } from '@mds/shared/types/booking';

import { useSession } from '@/auth/use-session';
import * as bookings from '@/bookings/bookings.api';
import BookingDetails from '@/components/booking/booking-details';
import BookingsMonth, { type CalendarDay } from '@/components/booking/bookings-month';
import PaymentFields from '@/components/booking/payment-fields';
import CustomerPicker from '@/components/customers/customer-picker';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { controlCls, inputCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Select from '@mds/shared/ui/select';
import { Table, Row, Cell } from '@/components/ui/table';
import { SegmentedToggle, Toolbar } from '@/components/ui/toolbar';
import {
  blankPaymentDraft,
  bookingStatusLabel,
  bookingStatusTone,
  bookingSpace,
  bookingWhen,
  cancelSchema,
  collectableAmount,
  eligibilityPayload,
  isBookingConflict,
  isPaymentStateChanged,
  paymentSchema,
  type PaymentDraft,
} from '@/domain/bookings';
import { keyOf, monthTitle } from '@mds/shared/lib/dates';
import { formatCents } from '@/lib/money';
import { useValidation } from '@/lib/validation';
import * as resources from '@/resources/resources.api';

const ALL = 'All';
const PAGE_SIZE = 25;

const SORTS = [
  { value: 'createdAt:desc', label: 'Sort: newest added' },
  { value: 'createdAt:asc', label: 'Sort: oldest added' },
  { value: 'bookingDate:asc', label: 'Sort: earliest date' },
  { value: 'bookingDate:desc', label: 'Sort: latest date' },
  { value: 'customerName:asc', label: 'Sort: customer A–Z' },
  { value: 'customerName:desc', label: 'Sort: customer Z–A' },
  { value: 'balanceAmountMinor:desc', label: 'Sort: highest balance' },
  { value: 'balanceAmountMinor:asc', label: 'Sort: lowest balance' },
  { value: 'reference:asc', label: 'Sort: reference A–Z' },
  { value: 'reference:desc', label: 'Sort: reference Z–A' },
];

const BOOKING_STATUSES = [
  { value: ALL, label: 'All statuses' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
];

/**
 * Every booking the API holds, as a list or a month calendar.
 *
 * Filtering, sorting and paging all happen on the server, so the page holds one
 * page of rows. Bookings are made on Spaces, where availability is read under a
 * lock before anything is written — this screen reads, collects and cancels.
 *
 * The calendar reads the month a day at a time: the list endpoint filters by
 * one date, not a range, so each day of the month shown is its own request —
 * cached, and made only while the calendar is open.
 */
export default function BookingsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { can } = useSession();
  const mayManage = can('reservations.manage');
  const mayRecord = can('payments.record');
  const maySeeCustomers = can('crm.customers.read');

  const [params, setParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [date, setDate] = useState('');
  const [resourceId, setResourceId] = useState(ALL);
  const [bookingStatus, setBookingStatus] = useState<string>(ALL);
  // Set by the customer record, which links here to show one customer's history.
  const [customerId, setCustomerId] = useState(params.get('customerId') ?? ALL);
  const [sort, setSort] = useState('createdAt:desc');
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [month, setMonth] = useState(() => new Date());

  // Set by a converted proposal, which links here to open the booking it made.
  const [detailId, setDetailId] = useState<string | null>(() => params.get('bookingId'));
  const [collecting, setCollecting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [draft, setDraft] = useState<PaymentDraft>(blankPaymentDraft);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const [sortKey, direction] = sort.split(':') as [
    'bookingDate' | 'createdAt' | 'reference' | 'customerName' | 'balanceAmountMinor',
    'asc' | 'desc',
  ];

  const list = useQuery({
    queryKey: ['bookings', { page, date, resourceId, customerId, bookingStatus, sort }],
    queryFn: () =>
      bookings.listBookings({
        page,
        limit: PAGE_SIZE,
        date: date || undefined,
        resourceId: resourceId === ALL ? undefined : resourceId,
        customerId: customerId === ALL ? undefined : customerId,
        bookingStatus: bookingStatus === ALL ? undefined : (bookingStatus as BookingListStatus),
        sort: sortKey,
        direction,
      }),
    enabled: view === 'list',
  });

  /** Every day of the month shown, as the list endpoint's `date`. */
  const monthDays = useMemo(() => {
    const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    return Array.from({ length: count }, (_, i) =>
      keyOf(new Date(month.getFullYear(), month.getMonth(), i + 1)),
    );
  }, [month]);

  const dayReads = useQueries({
    queries: monthDays.map((day) => ({
      queryKey: ['bookings', 'day', { day, resourceId, customerId, bookingStatus }],
      queryFn: () =>
        bookings.listBookings({
          page: 1,
          limit: 100,
          date: day,
          resourceId: resourceId === ALL ? undefined : resourceId,
          customerId: customerId === ALL ? undefined : customerId,
          bookingStatus: bookingStatus === ALL ? undefined : (bookingStatus as BookingListStatus),
          sort: 'bookingDate',
          direction: 'asc',
        }),
      enabled: view === 'calendar',
    })),
  });

  const calendarDays = useMemo(
    () =>
      Object.fromEntries(
        monthDays.map((day, i): [string, CalendarDay] => {
          const read = dayReads[i];
          return [
            day,
            {
              bookings: read?.data?.data ?? [],
              total: read?.data?.pagination.total ?? 0,
              pending: read?.isPending ?? true,
              failed: read?.isError ?? false,
            },
          ];
        }),
      ),
    [monthDays, dayReads],
  );
  const monthTotal = Object.values(calendarDays).reduce((n, d) => n + d.total, 0);
  const monthLoading = Object.values(calendarDays).some((d) => d.pending);

  const inventory = useQuery({
    queryKey: ['resources', { includeInactive: true }],
    queryFn: () => resources.listResources({ includeInactive: true }),
  });

  // The open booking is re-read on its own so collecting or cancelling shows
  // the server's new state without waiting for the whole page to refetch.
  const detail = useQuery({
    queryKey: ['booking', detailId],
    queryFn: () => bookings.getBooking(detailId as string),
    enabled: detailId !== null,
  });
  const booking = detail.data ?? null;

  const paymentRules = useMemo(() => paymentSchema(booking), [booking]);
  const paymentValidation = useValidation(paymentRules, draft);
  const cancelDraft = useMemo(() => ({ reason }), [reason]);
  const cancelValidation = useValidation(cancelSchema, cancelDraft);

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const pagination = list.data?.pagination;

  // Keep the deep link honest: clearing the filter should clear the URL too.
  useEffect(() => {
    const current = params.get('customerId') ?? ALL;
    if (current === customerId) return;
    const next = new URLSearchParams(params);
    if (customerId === ALL) next.delete('customerId');
    else next.set('customerId', customerId);
    setParams(next, { replace: true });
  }, [customerId, params, setParams]);

  // The booking link is a one-shot: once the modal closes it should not reopen
  // on a reload or when the filters change.
  useEffect(() => {
    if (detailId !== null || !params.has('bookingId')) return;
    const next = new URLSearchParams(params);
    next.delete('bookingId');
    setParams(next, { replace: true });
  }, [detailId, params, setParams]);

  const refresh = async (id: string | null) => {
    await queryClient.invalidateQueries({ queryKey: ['bookings'] });
    await queryClient.invalidateQueries({ queryKey: ['payments'] });
    if (id) await queryClient.invalidateQueries({ queryKey: ['booking', id] });
  };

  const openDetail = (b: AdminBooking) => {
    setDetailId(b.id);
    setProblem(null);
  };

  const startCollect = () => {
    setDraft(blankPaymentDraft);
    paymentValidation.reset();
    setProblem(null);
    setCollecting(true);
  };

  const collect = async () => {
    if (!booking) return;
    if (!paymentValidation.check()) return;
    setBusy(true);
    setProblem(null);
    try {
      await bookings.recordBookingPayment(booking.id, {
        amountMinor: collectableAmount(booking, draft.choice),
        expectedPaidAmountMinor: booking.payment.paidAmountMinor,
        method: draft.method,
        ...(draft.method === 'other' ? { methodDetail: draft.methodDetail.trim() } : {}),
        ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
        ...eligibilityPayload(booking, draft),
      });
      await refresh(booking.id);
      setCollecting(false);
    } catch (e) {
      // Both conflicts mean the page is holding a stale booking, so the fix is
      // the same: re-read it and let the administrator look again.
      if (isPaymentStateChanged(e) || isBookingConflict(e)) {
        await refresh(booking.id);
        setProblem(
          isPaymentStateChanged(e)
            ? 'Someone else took a payment on this booking. The figures have been reloaded. Check them before recording again.'
            : 'The desk or time was taken while this was open, so no payment was recorded.',
        );
      } else {
        setProblem(e instanceof ApiError ? e.message : 'That did not record. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!booking) return;
    if (!cancelValidation.check()) return;
    setBusy(true);
    setProblem(null);
    try {
      await bookings.cancelBooking(booking.id, reason.trim() ? { reason: reason.trim() } : {});
      await refresh(booking.id);
      setCancelling(false);
      setReason('');
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'That did not cancel. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const resourceOptions = [
    { value: ALL, label: 'All spaces' },
    ...(inventory.data ?? []).map((r) => ({ value: r.id, label: r.name })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <Card
        title={
          view === 'calendar'
            ? monthLoading
              ? monthTitle(month)
              : `${monthTotal} ${monthTotal === 1 ? 'booking' : 'bookings'} in ${monthTitle(month)}`
            : pagination
              ? `${pagination.total} ${pagination.total === 1 ? 'booking' : 'bookings'}`
              : 'All bookings'
        }
        action={
          <Toolbar>
            <SegmentedToggle
              value={view}
              options={['list', 'calendar'] as const}
              onChange={setView}
            />
            {view === 'list' && (
              <input
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setPage(1);
                }}
                aria-label="Filter by date"
                className={controlCls + ' w-[160px]'}
              />
            )}
            <Select
              value={resourceId}
              onChange={(v) => {
                setResourceId(v);
                setPage(1);
              }}
              options={resourceOptions}
              aria-label="Filter by space"
            />
            <Select
              value={bookingStatus}
              onChange={(value) => {
                setBookingStatus(value);
                setPage(1);
              }}
              options={BOOKING_STATUSES}
              aria-label="Filter by booking status"
            />
            {/* Searched on the server: the directory is far past anything a
                dropdown could hold, and `ALL` is this page's empty. */}
            {maySeeCustomers && (
              <CustomerPicker
                value={customerId === ALL ? '' : customerId}
                onChange={(v) => {
                  setCustomerId(v || ALL);
                  setPage(1);
                }}
                anyLabel="All customers"
                placeholder="All customers"
                className="w-[220px]"
                aria-label="Filter by customer"
              />
            )}
            {view === 'list' && (
              <Select
                value={sort}
                onChange={(v) => {
                  setSort(v);
                  setPage(1);
                }}
                options={SORTS}
                aria-label="Sort bookings"
              />
            )}
            {/* Booking needs a live availability check, which lives on Spaces. */}
            {mayManage && <Button onClick={() => navigate('/spaces')}>+ New booking</Button>}
          </Toolbar>
        }
      >
        {view === 'calendar' ? (
          <BookingsMonth
            month={month}
            onMonthChange={setMonth}
            days={calendarDays}
            onOpen={openDetail}
            onShowDay={(day) => {
              setDate(day);
              setPage(1);
              setView('list');
            }}
          />
        ) : list.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading bookings…</p>
        ) : list.isError ? (
          <EmptyState
            title="Could not load the bookings"
            hint={
              list.error instanceof ApiError
                ? list.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No bookings match"
            hint="Try a different status, date, space, or customer, or clear the filters."
          />
        ) : (
          <>
            <Table head={['Ref', 'Customer', 'Space', 'When', 'Total', 'Balance', 'Status']}>
              {rows.map((b) => (
                <Row key={b.id} onClick={() => openDetail(b)}>
                  <Cell className="font-mono text-md text-ink-muted">{b.reference}</Cell>
                  <Cell className="font-semibold">{b.customer.name}</Cell>
                  <Cell>{bookingSpace(b)}</Cell>
                  <Cell className="font-mono text-md">{bookingWhen(b)}</Cell>
                  <Cell className="font-semibold tabular-nums">
                    {formatCents(b.payment.totalAmountMinor)}
                  </Cell>
                  <Cell
                    className={
                      'tabular-nums ' +
                      (b.payment.balanceAmountMinor > 0
                        ? 'font-semibold text-warn-text'
                        : 'text-ink-muted')
                    }
                  >
                    {b.payment.balanceAmountMinor > 0
                      ? formatCents(b.payment.balanceAmountMinor)
                      : '—'}
                  </Cell>
                  <Cell>
                    <Chip className={'border ' + bookingStatusTone(b.status)}>
                      {bookingStatusLabel(b.status)}
                    </Chip>
                  </Cell>
                </Row>
              ))}
            </Table>

            {pagination && pagination.totalPages > 1 && (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-md text-ink-muted">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="quiet"
                    disabled={pagination.page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={!pagination.hasMore}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        open={detailId !== null && !collecting && !cancelling}
        onClose={() => setDetailId(null)}
        title={booking ? `${booking.customer.name} · ${booking.reference}` : 'Booking'}
      >
        {detail.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading the booking…</p>
        ) : detail.isError || !booking ? (
          <Banner>
            {detail.error instanceof ApiError
              ? detail.error.message
              : 'That booking could not be read.'}
          </Banner>
        ) : (
          <>
            {problem && <Banner>{problem}</Banner>}
            <BookingDetails
              booking={booking}
              busy={busy}
              onCollect={
                mayRecord &&
                booking.payment.balanceAmountMinor > 0 &&
                booking.lifecycleStatus !== 'cancelled'
                  ? startCollect
                  : undefined
              }
              onCancel={
                mayManage && booking.lifecycleStatus !== 'cancelled'
                  ? () => {
                      setReason('');
                      cancelValidation.reset();
                      setProblem(null);
                      setCancelling(true);
                    }
                  : undefined
              }
            />
          </>
        )}
      </Modal>

      <Modal
        open={collecting && booking !== null}
        onClose={() => setCollecting(false)}
        title="Record payment"
        footer={
          <>
            <Button onClick={() => void collect()} disabled={busy}>
              {busy ? 'Recording…' : 'Record payment'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setCollecting(false)}>
              Cancel
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        {booking && (
          <PaymentFields
            booking={booking}
            draft={draft}
            onChange={setDraft}
            field={paymentValidation.field}
          />
        )}
      </Modal>

      <Modal
        open={cancelling && booking !== null}
        onClose={() => setCancelling(false)}
        title="Cancel this booking"
        footer={
          <>
            <Button variant="danger" onClick={() => void cancel()} disabled={busy}>
              {busy ? 'Cancelling…' : 'Cancel booking'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setCancelling(false)}>
              Keep it
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        <p className="m-0 text-base text-ink-secondary">
          The desk or time is released and the booking is kept with its history. Any deposit already
          taken is non-refundable and stays on the record. There is no refund action.
        </p>
        <Field
          label="Reason"
          hint="Optional, and written to the audit log."
          {...cancelValidation.field('reason')}
        >
          <input
            className={inputCls}
            value={reason}
            maxLength={500}
            placeholder="Customer cancelled by phone"
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </Modal>
    </div>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
    >
      {children}
    </p>
  );
}
